import {
  AnimationCallbackEvent,
  Directive,
  effect,
  ElementRef,
  inject,
  model,
  OnInit,
  output,
  signal,
} from '@angular/core';

import { SingleOrArray } from '@plugins/shared/types';
import { returnAsArray } from '@plugins/shared/utils/arrayUtils/array-utils';

export type AnimationState = 'enter' | 'leave' | (string & {});

export type SettableAnimationState<T extends AnimationsConfig> = Exclude<
  Extract<keyof T, string>,
  'enter' | 'leave'
>;

export type DoneAnimationState<T extends AnimationsConfig> = Exclude<
  Extract<keyof T, string>,
  'leave'
>;

export type AnimationType =
  | 'fade'
  | 'growX'
  | 'growY'
  | 'slideTop'
  | 'slideBottom'
  | 'slideLeft'
  | 'slideRight'
  | 'drop';

const animationTransitions: Record<AnimationType, PropertyIndexedKeyframes> = {
  fade: { opacity: [0, 1] },
  growX: { gridTemplateColumns: ['0fr', '1fr'] },
  growY: { gridTemplateRows: ['0fr', '1fr'] },
  slideTop: { transform: ['translateY(-100%)', 'translateY(0)'] },
  slideBottom: { transform: ['translateY(100%)', 'translateY(0)'] },
  slideLeft: { transform: ['translateX(-100%)', 'translateX(0)'] },
  slideRight: { transform: ['translateX(100%)', 'translateX(0)'] },
  drop: { bottom: ['-50px', '0px'] },
};

export type AnimationConfig = {
  type?: AnimationType | AnimationType[];
  keyframes?: PropertyIndexedKeyframes;
  timingOptions?: KeyframeAnimationOptions;
};

export type AnimationsConfig<T extends AnimationState = AnimationState> =
  Partial<Record<T, SingleOrArray<AnimationConfig> | null>>;

type PropertyIndexedKeyframesValue =
  | string
  | string[]
  | number
  | null
  | (number | null)[]
  | undefined;

@Directive({
  selector: '[pluginsAnimation]',
  host: {
    '(animate.leave)': 'onLeave($event)',
  },
})
export class AnimationDirective<T extends AnimationsConfig> implements OnInit {
  readonly pluginsAnimation = model<T>();
  readonly pluginsAnimationState = model<SettableAnimationState<T>>();
  readonly pluginsAnimationWithInitialState = model(true);
  readonly pluginsAnimationDisabled = model(false);

  readonly pluginsAnimationDone = output<DoneAnimationState<T>>();

  private elemRef = inject(ElementRef) as ElementRef<HTMLElement>;

  private fromState: keyof T | null = null;
  private readonly enterDone = signal(false);

  animation: {
    play: () => void;
    pause: () => void;
    finish: () => void;
    finished: Promise<boolean>;
  } | null = null;

  ngOnInit() {
    if (this.pluginsAnimationDisabled() || !this.pluginsAnimation()?.enter) {
      this.enterDone.set(true);
      return;
    }
    this.animation = this.animate(
      this.pluginsAnimation()?.enter as SingleOrArray<AnimationConfig>,
    );
    void this.animation?.finished.then(() => {
      this.enterDone.set(true);
      this.pluginsAnimationDone.emit('enter' as DoneAnimationState<T>);
    });
  }

  onLeave(event: AnimationCallbackEvent) {
    if (
      this.pluginsAnimationDisabled() ||
      this.pluginsAnimation()?.leave === null ||
      (this.pluginsAnimation()?.enter === undefined &&
        this.pluginsAnimation()?.leave === undefined)
    ) {
      event.animationComplete();
      return;
    }
    if (
      this.pluginsAnimation()?.leave === undefined &&
      this.pluginsAnimation()?.enter
    ) {
      this.animation = this.animate(
        returnAsArray(
          this.pluginsAnimation()?.enter as SingleOrArray<AnimationConfig>,
        ).map((animation) => ({
          ...animation,
          timingOptions: {
            ...(animation.timingOptions ?? {}),
            direction: 'reverse',
          },
        })),
      );
      void this.animation?.finished.then(() => {
        event.animationComplete();
      });
      return;
    }
    this.animation = this.animate(
      this.pluginsAnimation()?.leave as SingleOrArray<AnimationConfig>,
    );
    void this.animation?.finished.then(() => {
      event.animationComplete();
    });
  }

  private readonly animationStateEffect = effect(() => {
    const state = this.pluginsAnimationState() ?? null;
    const enterDone = this.enterDone();
    if (!state || this.fromState === state || !enterDone) {
      return;
    }
    const animationConfig = this.pluginsAnimation()?.[state as string];
    if (!animationConfig) {
      return;
    }
    const prevState = this.fromState;
    this.fromState = state;
    if (
      !prevState &&
      this.pluginsAnimationWithInitialState() &&
      !this.pluginsAnimation()?.enter
    ) {
      this.setElementAnimationConfigState(animationConfig);
      return;
    }
    if (this.pluginsAnimationDisabled()) {
      return;
    }
    this.animation?.finish();
    this.animation = this.animate(animationConfig);
    void this.animation?.finished.then(() => {
      this.pluginsAnimationDone.emit(state as DoneAnimationState<T>);
    });
  });

  private getAnimationPropertyIndexedKeyframes(
    animationConfig: AnimationConfig,
  ): PropertyIndexedKeyframes {
    const typeKeyframes = returnAsArray(
      animationConfig.type ?? [],
    ).reduce<PropertyIndexedKeyframes>((acc, type) => {
      return { ...acc, ...animationTransitions[type] };
    }, {});
    return { ...typeKeyframes, ...(animationConfig.keyframes ?? {}) };
  }

  private checkGrowAnimation(animationConfig: SingleOrArray<AnimationConfig>) {
    for (const animation of returnAsArray(animationConfig)) {
      if (animation.type) {
        const types = returnAsArray(animation.type);
        if (types.includes('growX')) {
          this.elemRef.nativeElement.classList.add('plugins-grow-x');
        } else if (types.includes('growY')) {
          this.elemRef.nativeElement.classList.add('plugins-grow-y');
        }
      }
    }
  }

  private setElementAnimationConfigState(
    animationConfig: SingleOrArray<AnimationConfig>,
  ) {
    const styles: Record<string, PropertyIndexedKeyframesValue> = {};
    for (const animation of returnAsArray(animationConfig)) {
      const keyframes = this.getAnimationPropertyIndexedKeyframes(animation);
      for (const [style, value] of Object.entries(keyframes)) {
        if (value) {
          const values = returnAsArray(value);
          styles[style] =
            animation.timingOptions?.direction === 'reverse'
              ? values[0]
              : values.at(-1);
        }
      }
    }
    this.checkGrowAnimation(animationConfig);
    Object.assign(this.elemRef.nativeElement.style, styles);
  }

  private animate(animationConfig: SingleOrArray<AnimationConfig>) {
    this.checkGrowAnimation(animationConfig);
    const animations = returnAsArray(animationConfig).map((animation) => {
      return this.elemRef.nativeElement.animate(
        this.getAnimationPropertyIndexedKeyframes(animation),
        animation.timingOptions,
      );
    });
    return {
      play: () => animations.forEach((animation) => animation.play()),
      pause: () => animations.forEach((animation) => animation.pause()),
      finish: () => animations.forEach((animation) => animation.finish()),
      finished: Promise.allSettled(
        animations.map((animation) => animation.finished),
      ).then(() => true),
    };
  }
}
